import React from 'react';
import _ from 'underscore';

import NotificationDirectIcon from '@atlaskit/icon/glyph/notification-direct';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { isEmbeddedInAtlassian } from '@trello/browser';
import { N30, N900 } from '@trello/colors';
import { ErrorBoundary, sendErrorEvent } from '@trello/error-reporting';
// eslint-disable-next-line no-restricted-imports
import $ from '@trello/jquery';
import { PremiumFeature } from '@trello/product-features';
import ReactDOM from '@trello/react-dom-wrapper';

// eslint-disable-next-line no-restricted-imports
import { Controller } from 'app/scripts/controller';
import { navigate } from 'app/scripts/controller/navigate';
import { getBoardUrl } from 'app/scripts/controller/urls';
import { InspiringBoardsWorkspaceIds } from 'app/scripts/data/inspiring-boards-workspace-ids';
import { Auth } from 'app/scripts/db/Auth';
import { l } from 'app/scripts/lib/localize';
import { WindowSize } from 'app/scripts/lib/window-size';
import { BoardCleanUpView } from 'app/scripts/views/board/BoardCleanUpView';
import { Dialog } from 'app/scripts/views/lib/Dialog';
import { BoardHeaderTemplate } from 'app/scripts/views/templates/BoardHeaderTemplate';
import { SingleBoardCalendarView } from 'app/src/components/BoardCalendarView';
import { LazyBoardDescriptionSpotlight } from 'app/src/components/BoardDescriptionSpotlight';
import { wasShowSidebarSpotlightViewedState } from 'app/src/components/BoardDescriptionSpotlight/useWasShowSidebarSpotlightViewed';
import { BoardHeader } from 'app/src/components/BoardHeader';
import { BoardHeaderShowSidebarButton } from 'app/src/components/BoardHeaderShowSidebarButton';
import { BoardShareButton } from 'app/src/components/BoardShareButton';
import { AutomaticReports } from 'app/src/components/Butler';
import { ButlerBoardButtons } from 'app/src/components/Butler/Buttons/ButlerBoardButtons';
import { renderComponent } from 'app/src/components/ComponentWrapper';
import { BoardFilterPopoverButton } from 'app/src/components/FilterPopover';
import { filteredCardsCountState } from 'app/src/components/FilterPopover/filteredCardsCountState';
import { Null } from 'app/src/components/Null';
import { SingleBoardTableView } from 'app/src/components/TableView/SingleBoardTableView';
import { TimelineViewWrapper } from 'app/src/components/TimelineViewWrapper';
import { LazyViewSwitcher as ViewSwitcher } from 'app/src/components/ViewSwitcher';
import { stopPropagationAndPreventDefault } from 'app/src/stopPropagationAndPreventDefault';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderBoardHeader = function (this: any) {
  const isPremOrgAdmin = this.model.isPremOrgAdmin(Auth.me());

  const shouldRenderPartialReactBoardHeader = this
    .shouldRenderPartialReactBoardHeader;

  if (shouldRenderPartialReactBoardHeader) {
    this.renderReactBoardHeader();
    return;
  }

  const data = {
    name: this.model.get('name'),
    canRename: isPremOrgAdmin || this.model.owned(),
    isLoggedIn: Auth.isLoggedIn(),
    isUntitled: this.model.get('name') === l(['untitled board']),
    canJoin: this.model.canJoin(),
    canInviteMembers: this.model.canInviteMembers(),
    canAssign: this.model.editable(),
    isEmbeddedInAtlassian: isEmbeddedInAtlassian(),
    isTemplate: this.model.isTemplate(),
    hasEditPrivileges: this.model.editable(),
  };

  // @ts-expect-error TS(2339): Property 'starTooltipKey' does not exist on type '... Remove this comment to see the full error message
  data.starTooltipKey = this.model.isTemplate()
    ? 'click-to-star-or-unstar-this-template-starred-templates-show-up-at-the-top-of-your-boards-list'
    : 'click-to-star-or-unstar-this-board-starred-boards-show-up-at-the-top-of-your-boards-list';

  const organization = this.model.getOrganization();
  if (organization) {
    // @ts-expect-error TS(2339): Property 'showCopyButton' does not exist on type '... Remove this comment to see the full error message
    data.showCopyButton = _.contains(
      InspiringBoardsWorkspaceIds,
      organization.id,
    );
  }

  // Work-around to avoid data loss from re-populating header and
  // over-writing filtering react component
  const britaReactRoot = this.el.querySelector(
    '.js-brita-board-filter-btn-container',
  );

  $('.js-board-header', this.$el).html(BoardHeaderTemplate(data));

  const newBritaReactRoot = this.el.querySelector(
    '.js-brita-board-filter-btn-container',
  );

  if (britaReactRoot) {
    newBritaReactRoot.parentNode.replaceChild(
      britaReactRoot,
      newBritaReactRoot,
    );
  }

  this.renderShowSidebarButton();
  this.renderBoardShareButton();
  this.renderBritaBoardFilterButton();
  this.renderPermissionLevel();
  this.renderBoardHeaderCalendar();
  this.renderBoardHeaderSubscribed();
  this.renderBoardHeaderMap();
  this.renderFilteringStatus();
  this.renderBoardHeaderStarred();
  this.renderPluginButtons();
  this.renderButlerBoardButtons();
  this.renderBoardHeaderFacepile();
  this.renderBoardTemplateBadge();
  this.renderWorkspacesAutoNameAlert();
  this.renderPluginDropdownList();
  this.renderPluginHeaderButtons();

  this.renderViewSwitcher();
  this.renderSubscribeIcon();

  return this;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderSubscribeIcon = function (this: any) {
  const element = this.el.querySelector(
    '.board-header-subscribe-icon-container',
  );

  if (element) {
    this.unmountSubscribedToBoardHeaderIcon = renderComponent(
      <NotificationDirectIcon label="" size="small" />,
      element,
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeSubscribedToBoardHeaderIcon = function (this: any) {
  this.unmountSubscribedToBoardHeaderIcon?.();
  this.unmountSubscribedToBoardHeaderIcon = null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderReactBoardHeader = function (this: any) {
  const headerTarget = this.el.querySelector('.js-board-header');

  if (!headerTarget) {
    return;
  }

  const sidebarVisibile = this.model.viewState.get('showSidebar');
  const showSidebar = () => this.model.viewState.setShowSidebar(true);
  renderComponent(
    <BoardHeader
      boardId={this.model.id}
      legacyBoardModel={this.model}
      sidebarVisible={sidebarVisibile}
      // eslint-disable-next-line react/jsx-no-bind
      showSidebar={showSidebar}
    />,
    headerTarget,
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeReactBoardHeader = function (this: any) {
  if ($('.js-board-header', this.$el).length) {
    return ReactDOM.unmountComponentAtNode(
      this.el.querySelector('.js-board-header'),
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderBoardHeaderStarred = function (this: any) {
  if (this.model.isStarred()) {
    $('.js-star-board > .board-header-btn-icon', this.$el).removeClass(
      'icon-star',
    );
    $('.js-star-board > .board-header-btn-icon', this.$el).addClass(
      'icon-starred',
    );
  } else {
    $('.js-star-board > .board-header-btn-icon', this.$el).removeClass(
      'icon-starred',
    );
    $('.js-star-board > .board-header-btn-icon', this.$el).addClass(
      'icon-star',
    );
  }
  return this;
};

// always show hollow star on hover
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const hoverStar = function (this: any) {
  $('.js-star-board > .board-header-btn-icon', this.$el).removeClass(
    'icon-starred',
  );
  $('.js-star-board > .board-header-btn-icon', this.$el).addClass('icon-star');
};

// active state when clicking on state (onmousedown)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const clickStar = function (this: any) {
  if (this.model.isStarred()) {
    $('.js-star-board > .board-header-btn-icon', this.$el).removeClass(
      'icon-starred',
    );
    $('.js-star-board > .board-header-btn-icon', this.$el).addClass(
      'icon-star',
    );
  } else {
    $('.js-star-board > .board-header-btn-icon', this.$el).removeClass(
      'icon-star',
    );
    $('.js-star-board > .board-header-btn-icon', this.$el).addClass(
      'icon-starred',
    );
  }
  return this;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toggleStarred = function (this: any) {
  const { boardStarList } = Auth.me();
  const boardStar = boardStarList.getBoardStar(this.model.id);
  if (boardStar) {
    boardStarList.unstarBoard(this.model.id);
  } else {
    boardStarList.starBoard(this.model.id);
  }

  Analytics.sendUpdatedBoardFieldEvent({
    field: 'star',
    value: !!boardStar,
    source: 'boardScreen',
    containers: {
      board: {
        id: this.model.id,
      },
    },
  });

  return this;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const clearFilters = function (this: any, e: any) {
  stopPropagationAndPreventDefault(e);
  Analytics.sendClickedButtonEvent({
    buttonName: 'clearBoardFilterButton',
    source: 'boardScreen',
    containers: {
      board: {
        id: this.model.id,
      },
      organization: {
        id: this.model.getOrganization()?.id,
      },
      enterprise: {
        id: this.model.getEnterprise()?.id,
      },
    },
  });

  this.model.filter.clear();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const showCleanUp = function (this: any, e: any) {
  stopPropagationAndPreventDefault(e);

  // @ts-expect-error TS(2351): This expression is not constructable.
  const view = new BoardCleanUpView({
    model: this.model,
    modelCache: this.modelCache,
  });
  Dialog.show({
    view,
    maxWidth: 800,
    hide() {
      return view.abort();
    },
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderShowSidebarButton = function (this: any) {
  const node = this.el.querySelector('.show-sidebar-button-react-root');

  if (node) {
    // We need a solid opaque background color for the target so it doesn't get washed
    // out by the spotlight overlay.
    const targetBackgroundColor =
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      this.model.getPref('backgroundBrightness') === 'light' ? N30 : N900;
    this.unmountBoardDescriptionSpotlight?.();

    this._onClickNext = () => {
      wasShowSidebarSpotlightViewedState.setValue(true);
      this.setSidebarShow();
    };
    this._isSidebarOpen = () => this.model?.viewState.get('showSidebar');
    this._setSidebarShow = () => this.setSidebarShow();
    this.unmountBoardDescriptionSpotlight = renderComponent(
      <LazyBoardDescriptionSpotlight
        targetName="showSidebarButton"
        target={
          <BoardHeaderShowSidebarButton
            showSidebar={this._setSidebarShow}
            eventContainers={{
              board: {
                id: this.model.id,
              },
              organization: {
                id: this.model.getOrganization()?.id,
              },
            }}
          />
        }
        targetBgColor={targetBackgroundColor}
        isSidebarOpen={this._isSidebarOpen}
        onClickNext={this._onClickNext}
        eventContainers={formatContainers({
          idBoard: this.model.id,
          idOrganization: this.model.getOrganization()?.id,
          idEnterprise: this.model.getEnterprise()?.id,
        })}
        boardId={this.model.id}
      />,
      node,
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeShowSidebarButton = function (this: any) {
  this.unmountBoardDescriptionSpotlight?.();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderBoardShareButton = function (this: any) {
  const node = this.el.querySelector('.board-share-button');
  if (node) {
    renderComponent(
      <BoardShareButton
        idBoard={this.model.id}
        idOrg={this.model.getOrganization()?.id}
      />,
      node,
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeBoardShareButton = function (this: any) {
  if ($('.board-share-button', this.$el).length) {
    return ReactDOM.unmountComponentAtNode(
      this.el.querySelector('.board-share-button'),
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderBritaBoardFilterButton = function (this: any) {
  const targetNodeForButton = this.el.querySelector(
    '.js-brita-board-filter-btn-container',
  );

  if (!targetNodeForButton) {
    return;
  }

  renderComponent(
    <BoardFilterPopoverButton idBoard={this.model.id} />,
    targetNodeForButton,
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeBritaBoardFilterButton = function (this: any) {
  const container = this.el.querySelector(
    '.js-brita-board-filter-btn-container',
  );
  if (container) {
    ReactDOM.unmountComponentAtNode(container);
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderViewSwitcher = function (this: any) {
  const targetNodeForSwitcherInHeader = this.el.querySelector(
    '.js-board-views-switcher',
  );

  if (!targetNodeForSwitcherInHeader) {
    return;
  } else {
    renderComponent(
      <ErrorBoundary
        tags={{
          ownershipArea: 'trello-workflowers',
          // @ts-expect-error TS(2769): No overload matches this call.
          feature: 'View Switcher',
        }}
        errorHandlerComponent={Null}
      >
        <ViewSwitcher idBoard={this.model.id} />
      </ErrorBoundary>,
      targetNodeForSwitcherInHeader,
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const removeBoardViewsButton = function (this: any) {
  if ($('.js-board-views-switcher', this.$el).length) {
    return ReactDOM.unmountComponentAtNode(
      this.el.querySelector('.js-board-views-switcher'),
    );
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const showTimeline = function (this: any) {
  const hasViewsFeature = this.model.isFeatureEnabled(PremiumFeature.Views);

  if (hasViewsFeature) {
    $('#board', this.$el).addClass('hide');
    const container = this.el.querySelector('.js-board-view-container');
    renderComponent(
      <TimelineViewWrapper
        idBoard={this.model.id}
        /* eslint-disable-next-line react/jsx-no-bind, @typescript-eslint/no-explicit-any */
        navigateToCard={(id: any, params: any) => {
          Controller.showCardDetail(this.modelCache.get('Card', id), params);
        }}
      />,
      container,
    );
  } else {
    const boardUrl = getBoardUrl(this.model.id);
    navigate(boardUrl, { trigger: true });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const showCalendarView = function (this: any) {
  const hasViewsFeature = this.model.isFeatureEnabled(PremiumFeature.Views);

  const canShowCalendarView = hasViewsFeature;

  if (canShowCalendarView) {
    $('#board', this.$el).addClass('hide');
    const container = this.el.querySelector('.js-board-view-container');
    renderComponent(
      <SingleBoardCalendarView
        idBoard={this.model.id}
        // eslint-disable-next-line react/jsx-no-bind
        navigateToCard={(id) => {
          // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
          Controller.showCardDetail(this.modelCache.get('Card', id));
        }}
      />,
      container,
    );
  } else {
    const boardUrl = getBoardUrl(this.model.id);
    // @ts-expect-error TS(2345): Argument of type '{ trigger: true; shouldTrack: bo... Remove this comment to see the full error message
    navigate(boardUrl, { trigger: true, shouldTrack: false });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const showTable = function (this: any) {
  const hasViewsFeature = this.model.isFeatureEnabled(PremiumFeature.Views);

  if (hasViewsFeature) {
    $('#board', this.$el).addClass('hide');

    const container = this.el.querySelector('.js-board-view-container');
    renderComponent(
      <SingleBoardTableView
        idBoard={this.model.id}
        // eslint-disable-next-line react/jsx-no-bind
        navigateToCard={(id) => {
          // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
          Controller.showCardDetail(this.modelCache.get('Card', id));
        }}
      />,
      container,
    );
  } else {
    const boardUrl = getBoardUrl(this.model.id);
    navigate(boardUrl, { trigger: true });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const closeSingleBoardView = function (this: any) {
  const viewContainer = this.el.querySelector('.js-board-view-container');
  if (viewContainer) {
    ReactDOM.unmountComponentAtNode(viewContainer);
  }
  $('#board', this.$el).removeClass('hide');
  $('.js-list-name-input', this.$el).trigger('autosize.resize', false);

  this.renderViewSwitcherDebounced();
  this.renderBritaBoardFilterButtonDebounced();
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const renderButlerBoardButtons = function (this: any) {
  const reactRoot = this.el.querySelector('.js-butler-header-btns');
  if (this.model?.editable()) {
    if (reactRoot) {
      const renderTraceId = Analytics.startTask({
        taskName: 'view-butlerButtons/board',
        source: 'boardScreen',
      });
      try {
        renderComponent(
          <ButlerBoardButtons
            idBoard={this.model.id}
            idOrganization={this.model.get('idOrganization')}
            renderTraceId={renderTraceId}
          />,
          reactRoot,
        );
      } catch (err) {
        sendErrorEvent(err, {
          tags: {
            ownershipArea: 'trello-workflowers',
            feature: 'Butler on Boards',
          },
        });
      }
    }
  }
};

export const navigateToAutomaticReports = function (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  this: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reportType: any,
) {
  const automaticReportView = getBoardUrl(
    this.model.id,
    `butler/reports/${reportType}`,
  );

  navigate(automaticReportView, { trigger: true });

  // Close the sidebar menu for window sizes that aren't extra large.

  // @ts-expect-error TS(2339): Property 'fExtraLarge' does not exist on type '{ c... Remove this comment to see the full error message
  if (!WindowSize.fExtraLarge) {
    this.sidebarView.hideSidebar();
  }
};

// This is actually pretty generic and just closes any overlays;
// if it ever needs reuse, feel free to rename it.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const closeAutomaticReports = function (this: any, idBoard: any) {
  const boardUrl = getBoardUrl(idBoard || this.model.id);
  navigate(boardUrl, { trigger: true });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const showAutomaticReports = function (this: any, reportType: any) {
  const idBoard = this.model.id;
  const container = this.el.querySelector('.js-board-view-container');
  $('#board', this.$el).addClass('hide');

  renderComponent(
    <AutomaticReports
      reportType={reportType}
      idBoard={idBoard}
      idOrganization={this.model.get('idOrganization')}
      boardName={this.model.get('name')}
      // eslint-disable-next-line react/jsx-no-bind
      closeView={() => closeAutomaticReports(idBoard)}
    />,
    container,
  );
  Analytics.sendViewedComponentEvent({
    componentType: 'overlay',
    // @ts-expect-error TS(2322): Type '"butlerReportBuilderOverlay"' is not assigna... Remove this comment to see the full error message
    componentName: 'butlerReportBuilderOverlay',
    source: 'butlerReportBuilderOverlay',
    attributes: {
      reportType,
    },
  });
};

// There are many things that could cause the filter count to change
const watchedEvents = [
  // We could learn about a new card that matches the filter
  'add:Card',
  // A card that matched the filter may have been deleted
  'remove:Card',
  // A card may change so that it is no a match (or isn't one anymore)
  'change:Card',
  // We could learn about a new list that has matches in it (e.g. a list may have been moved to this board)
  'add:List',
  // A list that had matches in it could have been moved off of the board
  'remove:List',
  // A list with matches could have been closed or re-opened
  'change:List',
].join(' ');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const initializeFilterCountMonitoring = function (this: any) {
  let isMonitoring = false;
  const updateCount = this.frameDebounce(() => {
    const boardCardCount = this.model.filter.isFiltering()
      ? this.model
          .openCards()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .filter((card: any) => this.model.filter.satisfiesFilter(card)).length
      : 0;

    // Keep the filtered cards count for the board in sync with a shared state,
    // to avoid an unnecessary API call for SBVs. If this gets migrated to GraphQL
    // and we have the data in cache, we can eventually deprecate this.
    if (boardCardCount !== filteredCardsCountState.value.count) {
      filteredCardsCountState.setValue({ count: boardCardCount });
    }
  });

  const updateMonitoring = () => {
    const isFiltering = this.model.filter.isFiltering();
    if (isFiltering) {
      if (!isMonitoring) {
        isMonitoring = true;
        // Listen for all of the watched events that happen in the ModelCache, including
        // (possibly) events related to other boards.  Some of the events won't affect the
        // count (e.g. if a card changes position, or we got an update about a card on another
        // board) but counting the cards isn't too expensive if we're debouncing it
        this.listenTo(this.modelCache, watchedEvents, updateCount);
      }
      // The filter changed, so the count may be different
      updateCount.call(this);
    } else if (!isFiltering && isMonitoring) {
      isMonitoring = false;
      this.stopListening(this.modelCache, watchedEvents, updateCount);
    }
  };

  // Only monitor the model cache if we're currently filtering
  this.listenTo(this.model.filter, 'change', updateMonitoring);
  updateMonitoring();
};
